// Story book — scroll-progress driver for the `story-book` section.
//
// The section ships three layouts (chosen in CSS by viewport + reduced-motion). The layout is
// CSS-first (media queries decide it on first paint — no JS-gated swap). This module:
//   • picks the matching behavioral mode (flip / carousel / static) to match the CSS layout,
//   • in FLIP mode: on scroll (rAF-throttled) maps the section's pinned scroll progress (0 → 1) onto
//     per-page vars the CSS animates from — `--sb-flip` (rotateY/rotateX), `--sb-turn` (sine shading
//     that peaks at 90°), `--sb-under`/`--sb-reveal` (cast shadow + media settle on the page beneath)
//     — plus a root `--sb-progress` (bar fill + fore-edge). Toggles `is-current` (content stagger,
//     ambient glow), keeps the arrows/ticks in sync, and toggles `inert`/`aria-hidden` so only the
//     visible page is in the tab order. Arrows, clickable ticks, and ←/→ keys scroll the native
//     track to a page (no scroll-hijacking),
//   • in CAROUSEL mode (≤749px): builds dot controls, keeps the active dot + page label in sync with
//     swipes, and marks the centered card `is-current` for the presence treatment,
//   • in STATIC mode (reduced-motion desktop): does nothing — the pages read as a normal stack,
//   • for "video" pages: pauses every off-screen autoplay video and plays only the active page's
//     (`video.story-book__video`), so a stack of pages never runs N videos at once.
//
// Compositor-only visual work (transforms via CSS custom properties); the only layout read is a
// cached getBoundingClientRect in the rAF callback. Re-evaluates its mode on resize / motion change.

import { mountInstances } from '@theme/ca-section-instances';

(() => {
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  class StoryBook {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.story-book__track');
      this.stack = root.querySelector('.story-book__stack');
      this.pages = Array.from(root.querySelectorAll('.story-book__page'));
      this.count = this.pages.length;
      this.flips = Math.max(1, this.count - 1);
      this.counter = root.querySelector('[data-sb-counter]');
      this.dotsHost = root.querySelector('[data-sb-dots]');
      this.dotsLabel = root.querySelector('[data-sb-dots-label]');
      this.status = root.querySelector('[data-sb-status]');
      this.ambient = Array.from(root.querySelectorAll('[data-sb-ambient]'));
      this.ticks = Array.from(root.querySelectorAll('[data-sb-tick]'));
      this.prevBtn = root.querySelector('[data-sb-prev]');
      this.nextBtn = root.querySelector('[data-sb-next]');
      this.hasVideo = !!root.querySelector('video.story-book__video');
      this.applyPageMeta();
      this.observePosters();

      this.mode = null;
      this.current = -1;
      this.carouselActive = -1;
      this.rafId = 0;
      // Cached flip-scroll metrics. track.offsetHeight (and window.innerHeight) don't change while
      // scrolling — only on viewport resize — so caching them keeps tick() free of a per-frame
      // layout read (the forced reflow Lighthouse flagged). Refreshed by measure() on resize.
      this.span = 1;

      this.onScroll = this.onScroll.bind(this);
      this.tick = this.tick.bind(this);
      this.onCarouselScroll = this.onCarouselScroll.bind(this);
      this.reevaluate = this.reevaluate.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.bindVideoToggles();
      this.bindNav();
      // ←/→ turn pages while focus is inside the section (flip mode only; never steals vertical keys).
      root.addEventListener('keydown', this.onKeydown);
      this.apply();

      // Gates the JS-driven flourishes (content stagger, ticks, arrows, carousel presence) so
      // no-JS readers get the plain, fully readable layouts.
      root.classList.add('story-book--live');

      // Mode only changes when a breakpoint / reduced-motion preference is crossed — listen to those
      // rather than every resize pixel (no scroll/flip thrash, and a finite listener set to tear down).
      this.listen(MOBILE, 'add');
      this.listen(REDUCE, 'add');
    }

    listen(mql, op) {
      const method = op === 'add' ? 'addEventListener' : 'removeEventListener';
      const legacy = op === 'add' ? 'addListener' : 'removeListener';
      if (mql[method]) mql[method]('change', this.reevaluate);
      else if (mql[legacy]) mql[legacy](this.reevaluate); // older Safari
    }

    pageLabel(i) {
      const label = this.pages[i]?.dataset.sbLabel;
      return label ? `Go to page ${i + 1}: ${label}` : `Go to page ${i + 1}`;
    }

    applyPageMeta() {
      this.pages.forEach((page, i) => {
        const color = page.dataset.sbAmbientColor;
        if (color && this.ambient[i]) this.ambient[i].style.setProperty('--sb-ambient', color);
      });
      this.ticks.forEach((tick, i) => tick.setAttribute('aria-label', this.pageLabel(i)));
    }

    observePosters() {
      if (!this.root.querySelector('video[data-poster]')) return;
      const promote = () => {
        this.root.querySelectorAll('video[data-poster]').forEach((video) => {
          video.setAttribute('poster', video.dataset.poster);
          video.removeAttribute('data-poster');
        });
      };
      if (!('IntersectionObserver' in window)) {
        promote();
        return;
      }
      this.posterObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.posterObserver.disconnect();
          promote();
        },
        { rootMargin: '1200px 0px' }
      );
      this.posterObserver.observe(this.root);
    }

    destroy() {
      this.posterObserver?.disconnect();
      this.teardown();
      this.root.removeEventListener('keydown', this.onKeydown);
      this.listen(MOBILE, 'remove');
      this.listen(REDUCE, 'remove');
    }

    /* ---- Reader agency: arrows, clickable ticks, arrow keys (flip mode) ---- */
    bindNav() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.turn(this.prevBtn, this.current - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.turn(this.nextBtn, this.current + 1));
      this.ticks.forEach((tickBtn) => {
        tickBtn.addEventListener('click', () => this.goToPage(Number(tickBtn.dataset.sbIndex)));
      });
    }

    turn(button, index) {
      if (button.getAttribute('aria-disabled') === 'true') return;
      this.goToPage(index);
    }

    onKeydown(event) {
      if (this.mode !== 'flip') return;
      // Leave keys alone on controls the visitor is operating (e.g. a controls-mode video seeks on ←/→).
      if (event.target && event.target.closest && event.target.closest('video, input, textarea, select')) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goToPage(this.current + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goToPage(this.current - 1);
      }
    }

    // Scroll the window so the pinned track sits exactly on page `index` — native scroll, no hijack.
    goToPage(index) {
      if (this.mode !== 'flip' || !this.track) return;
      const target = Math.min(this.count - 1, Math.max(0, index));
      if (target === this.current) return;
      const span = Math.max(1, this.track.offsetHeight - window.innerHeight);
      const trackTop = window.scrollY + this.track.getBoundingClientRect().top;
      const top = trackTop + (target / this.flips) * span;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    chooseMode() {
      if (this.count < 2) return 'static';
      if (MOBILE.matches) return 'carousel';
      if (REDUCE.matches) return 'static';
      return 'flip';
    }

    apply() {
      const next = this.chooseMode();
      if (next === this.mode) return;
      this.teardown();
      this.mode = next;

      if (next === 'flip') this.enableFlip();
      else if (next === 'carousel') this.enableCarousel();
      else this.enableStatic();
    }

    enableStatic() {
      // Reduced-motion: don't autoplay any video. Otherwise (single-page section) play the one page.
      if (REDUCE.matches) this.pauseAllVideos();
      else this.playOnly(0);
    }

    // Mark `index` the active page everywhere it matters: only it stays focusable + in the a11y tree.
    // Used by both flip and carousel so off-screen pages never trap keyboard/SR users.
    setActivePage(index) {
      this.pages.forEach((page, i) => {
        const hidden = i !== index;
        // Focus-loss guard: if focus sits inside a page about to go inert, move it to the active page
        // first so the browser doesn't silently drop focus to <body>.
        if (hidden && page.contains(document.activeElement)) {
          const active = this.pages[index];
          if (active) {
            active.setAttribute('tabindex', '-1');
            active.focus({ preventScroll: true });
          }
        }
        page.toggleAttribute('inert', hidden);
        if (hidden) page.setAttribute('aria-hidden', 'true');
        else page.removeAttribute('aria-hidden');
        // Drives the content stagger (flip) and carousel presence (mobile) in CSS.
        page.classList.toggle('is-current', !hidden);
      });

      // Ambient glow follows the open page.
      this.ambient.forEach((layer, i) => layer.classList.toggle('is-active', i === index));

      // Ticks + arrows mirror the position.
      this.ticks.forEach((tickBtn, i) => {
        if (i === index) tickBtn.setAttribute('aria-current', 'true');
        else tickBtn.removeAttribute('aria-current');
      });
      if (this.prevBtn) this.prevBtn.setAttribute('aria-disabled', String(index <= 0));
      if (this.nextBtn) this.nextBtn.setAttribute('aria-disabled', String(index >= this.count - 1));

      // Carousel chapter label: the page's own label if set, else "n / N".
      if (this.dotsLabel) {
        const label = this.pages[index]?.querySelector('.story-book__page-label');
        this.dotsLabel.textContent = label?.textContent.trim() || `${index + 1} / ${this.count}`;
      }

      if (this.counter) this.counter.textContent = String(index + 1);
      if (this.status) this.status.textContent = `Page ${index + 1} of ${this.count}`;
    }

    reevaluate() {
      this.apply();
      if (this.mode === 'flip') this.tick();
    }

    teardown() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.stack) this.stack.removeEventListener('scroll', this.onCarouselScroll);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = 0;
      this.current = -1;
      this.carouselActive = -1;
      this.pages.forEach((page) => {
        page.style.removeProperty('--sb-flip');
        page.style.removeProperty('--sb-turn');
        page.style.removeProperty('--sb-under');
        page.style.removeProperty('--sb-reveal');
        // Drop the dynamic stack override so the static --sb-page-z applies again outside flip mode.
        page.style.removeProperty('z-index');
        page.__sbZ = undefined;
        page.classList.remove('is-current');
        page.removeAttribute('inert');
        page.removeAttribute('aria-hidden');
      });
      this.root.style.removeProperty('--sb-progress');
      // Ambient back to the server-rendered state (first layer lit).
      this.ambient.forEach((layer, i) => layer.classList.toggle('is-active', i === 0));
      // Mirror that reset across every position indicator so a mode switch starts clean at page 1.
      this.ticks.forEach((tickBtn, i) => {
        if (i === 0) tickBtn.setAttribute('aria-current', 'true');
        else tickBtn.removeAttribute('aria-current');
      });
      if (this.prevBtn) this.prevBtn.setAttribute('aria-disabled', 'true');
      if (this.nextBtn) this.nextBtn.setAttribute('aria-disabled', String(this.count <= 1));
      if (this.counter) this.counter.textContent = '1';
      if (this.status) this.status.textContent = `Page 1 of ${this.count}`;
      if (this.dotsLabel) this.dotsLabel.textContent = '';
      if (this.dotsHost) this.dotsHost.replaceChildren();
      this.dots = null;
      this.pauseAllVideos();
    }

    /* ---- Off-screen video coordination ---- only manage autoplay (background) videos; leave
       controls-mode videos for the visitor to drive. Under reduced motion, nothing auto-plays. */
    playOnly(activeIndex) {
      if (!this.hasVideo) return;
      this.pages.forEach((page, i) => {
        page.querySelectorAll('video.story-book__video').forEach((video) => {
          if (i === activeIndex && !REDUCE.matches) {
            video.preload = 'auto'; // off-screen pages render preload="none"; load on activation
            const playing = video.play();
            if (playing && playing.catch) playing.catch(() => {});
          } else {
            video.pause();
          }
        });
      });
    }

    // Pause/play toggle for autoplay videos (WCAG 2.2.2 — auto-starting looping content needs a
    // visitor-operable pause). The button state mirrors the video's real play/pause state.
    bindVideoToggles() {
      if (!this.hasVideo) return;
      this.root.querySelectorAll('[data-sb-video-toggle]').forEach((btn) => {
        const video = btn.closest('.story-book__media')?.querySelector('video.story-book__video');
        if (!video) return;
        btn.addEventListener('click', () => {
          if (video.paused) {
            video.preload = 'auto';
            const playing = video.play();
            if (playing && playing.catch) playing.catch(() => {});
          } else {
            video.pause();
          }
        });
        const sync = () => {
          const playing = !video.paused;
          btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
          btn.classList.toggle('is-playing', playing);
          btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        };
        video.addEventListener('play', sync);
        video.addEventListener('pause', sync);
        sync();
      });
    }

    pauseAllVideos() {
      if (!this.hasVideo) return;
      this.pages.forEach((page) => {
        page.querySelectorAll('video.story-book__video').forEach((video) => video.pause());
      });
    }

    /* ------------------------------------------------------------------ Flip */
    enableFlip() {
      this.measure();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.tick();
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(this.tick);
    }

    // Resize is the only thing that changes the scroll span (track height / viewport height) —
    // refresh the cached value here so tick() never has to read layout mid-scroll.
    onResize() {
      this.measure();
      this.tick();
    }

    // Cache the one flip-scroll layout read (track.offsetHeight) and the viewport height it pairs
    // with into a ready-to-use span. Called on enable + every resize.
    measure() {
      if (!this.track) return;
      this.span = Math.max(1, this.track.offsetHeight - window.innerHeight);
    }

    tick() {
      this.rafId = 0;
      if (this.mode !== 'flip' || !this.track) return;

      // READ phase — every layout read happens here, before any style write below, so a write
      // never invalidates a subsequent read within the frame (no read-after-write reflow).
      // span (track height vs viewport) is cached by measure()/onResize; only the scroll offset
      // (getBoundingClientRect().top) genuinely changes per frame.
      const span = this.span;
      const scrolled = -this.track.getBoundingClientRect().top;
      const progress = Math.min(1, Math.max(0, scrolled / span));
      const pos = progress * this.flips; // 0 → flips

      // WRITE phase — from here down it's all style.setProperty / zIndex (compositor-only).

      for (let i = 0; i < this.count; i++) {
        const flip = Math.min(1, Math.max(0, pos - i));
        const page = this.pages[i];
        page.style.setProperty('--sb-flip', flip.toFixed(4));
        // Turn shading that peaks at 90° and breathes back out (sine of the flip).
        page.style.setProperty('--sb-turn', Math.sin(flip * Math.PI).toFixed(4));
        // Dynamic stacking. Each leaf flips around its left edge onto a left-hand pile; the static
        // z-order (--sb-page-z) would leave page 1 permanently on top of that pile, so every turned
        // page's back hides behind page 1's (you'd only ever see page 1's inscription/number). Instead:
        // turned leaves stack most-recent-on-top (z = i), unturned leaves keep the current page on top
        // (z = count − i), and the single leaf mid-turn rides above both. Only write on change.
        let z;
        if (flip >= 1) z = i;
        else if (flip <= 0) z = this.count - i;
        else z = this.count * 2;
        if (page.__sbZ !== z) {
          page.style.zIndex = String(z);
          page.__sbZ = z;
        }
        if (i > 0) {
          // The leaf ABOVE this page drives its reveal: cast shadow sweeps while the leaf is
          // mid-turn (sine), and the media settles from a slight zoom as the reveal completes.
          const above = Math.min(1, Math.max(0, pos - (i - 1)));
          page.style.setProperty('--sb-under', Math.sin(above * Math.PI).toFixed(4));
          page.style.setProperty('--sb-reveal', above.toFixed(4));
        }
      }

      // Root-level so the bar fill AND the fore-edge stack both inherit it.
      this.root.style.setProperty('--sb-progress', progress.toFixed(4));

      const cur = Math.min(this.count - 1, Math.max(0, Math.round(pos)));
      if (cur !== this.current) {
        this.current = cur;
        this.setActivePage(cur);
        this.playOnly(cur);
      }
    }

    /* -------------------------------------------------------------- Carousel */
    enableCarousel() {
      if (!this.dotsHost) return;
      const dots = this.pages.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'story-book__dot';
        dot.setAttribute('aria-label', this.pageLabel(i));
        if (i === 0) dot.setAttribute('aria-current', 'true');
        dot.addEventListener('click', () => {
          // Scroll the carousel itself (not the page) so the tap never nudges vertical scroll.
          const page = this.pages[i];
          const left = page.offsetLeft - (this.stack.clientWidth - page.offsetWidth) / 2;
          this.stack.scrollTo({ left, behavior: 'smooth' });
        });
        return dot;
      });
      this.dotsHost.replaceChildren(...dots);
      this.dots = dots;
      // Start at the first page so the dots and the visible cell agree after a resize/rotation.
      this.stack.scrollTo({ left: 0 });
      this.stack.addEventListener('scroll', this.onCarouselScroll, { passive: true });
      this.syncDots();
    }

    onCarouselScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0;
        if (this.mode === 'carousel') this.syncDots();
      });
    }

    syncDots() {
      if (!this.dots) return;
      const mid = this.stack.scrollLeft + this.stack.clientWidth / 2;
      let active = 0;
      let best = Infinity;
      this.pages.forEach((page, i) => {
        const center = page.offsetLeft + page.offsetWidth / 2;
        const dist = Math.abs(center - mid);
        if (dist < best) {
          best = dist;
          active = i;
        }
      });
      this.dots.forEach((dot, i) => {
        if (i === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (active !== this.carouselActive) {
        this.carouselActive = active;
        // Only the centered page stays focusable / in the a11y tree (off-screen cards drop out).
        this.setActivePage(active);
        this.playOnly(active);
      }
    }
  }

  mountInstances({
    selector: '.story-book',
    create: (root) => new StoryBook(root),
    prop: '__storyBook',
    lazyMargin: '1200px',
  });
})();
